// @flow
import moment from 'moment'
import getPlaceDetails from '../places/getPlaceDetails'
import allowedQueries from '../../constants/allowedQueries'
import { LOCATION_TYPE } from '../../constants/locationType'
import normalize from '../../utils/normalize'

// Type
type QueryAddress = {
  address: string,
  lat?: number,
  lng?: number,
  google_id?: string,
  place_id?: string,
  custom_name?: string,
  airport_name?: string,
}

// Poller helpers
export const isMoreComing = (response: any): boolean =>
  (response && response.more_coming) || false

export const isResultEmpty = (response: any): boolean =>
  !response || !response.results || !response.results.length

export const isOndemandResultEmpty = (response: any): boolean =>
  !response || !response.ride_types || !response.ride_types.length

/**
 * By given query address return address with lat/lng
 * @type {Function}
 */
export const resolvePlace = async (address: QueryAddress): Promise<any> => {
  const placeId = address.google_id || address.place_id
  if (!placeId || (address.lat && address.lng)) return address

  const fullPlace = await getPlaceDetails(placeId)
  return {
    ...address,
    lat: fullPlace.lat,
    lng: fullPlace.lng,
  }
}

/**
 * By given query object resolve place id and add lat/lng to the query
 * object.
 * @type {Function}
 */
export const resolvePlaces = async (query: {
  start_location?: QueryAddress,
  end_location?: QueryAddress,
}): Promise<any> => {
  if (!query.start_location && !query.end_location) {
    return query
  }

  const resultQuery = { ...query }

  if (query.start_location) {
    const betterStart = await resolvePlace(query.start_location)
    resultQuery.start_location = betterStart
  }

  if (query.end_location) {
    const betterEnd = await resolvePlace(query.end_location)
    resultQuery.end_location = betterEnd
  }

  return resultQuery
}

/**
 * Normalize query object for price updating
 * @param {Object}  Query object with old or new query params
 * @return {Object} Normalized query object with only new params
 * @private
 */
export const filterPriceQuery = (query: Object): Object => {
  const { optional_amenities: queryAmenities, ...baseQuery } = query || {}
  const newAmenities = (queryAmenities || []).slice()
  const hasAmenities = !!queryAmenities

  const transformers = {
    session_id: (val) => ({ search_id: val }),
    trip_id: (val) =>
      baseQuery.ondemand ? { ride_id: val } : { result_id: val },
    result_id: (val) =>
      baseQuery.ondemand ? { ride_id: val } : { result_id: val },
    return_session_id: (val) => ({ return_search_id: val }),
    return_trip_id: (val) => ({ return_result_id: val }),
    gratuity: (val) => ({ gratuity_percent: parseFloat(val) }),
    meet_and_greet: (val) => {
      val && newAmenities.push('meet_and_greet')
    },
    booking_fee: (val) => ({ commission: parseFloat(val) }),
    coupon_code: (val) => val && { coupon_code: val },
  }

  const normQuery = normalize(query, transformers)
  if (hasAmenities || newAmenities.length > 0) {
    normQuery.optional_amenities = newAmenities
  }

  return normQuery
}

/**
 * Filter search query to clear undesired params
 * @param  {Object} query Query to filter
 * @return {Object} Filtered query
 * @private
 */
export const filterSearchQuery = (query: Object): Object => {
  const normDate = (val, field) => {
    const momentDate =
      val instanceof moment ? val : moment(val, 'MM/DD/Y hh:mm A')
    if (!momentDate.isValid()) return null
    return { [field]: momentDate.format() }
  }
  const normLocation = (
    {
      value,
      display,
      place_id: placeId,
      favorite_id: favoriteId,
      favorite_source: favoriteSource,
      name,
      type,
      lat,
      lng,
    },
    customName
  ) => {
    const result: QueryAddress = {
      address: value || display,
      favorite_id: favoriteId,
      favorite_source: favoriteSource,
    }

    if ((lat >= 0 || lat < 0) && (lng >= 0 || lng < 0)) {
      result.lat = lat
      result.lng = lng
    }

    if (placeId) {
      result.google_id = placeId
    }

    if (display || customName) {
      result.custom_name = display || customName
    }

    // Needed for public transit searches
    if (type === LOCATION_TYPE.AIRPORT) {
      result.airport_name = name
    }

    return result
  }
  const transformers = {
    start_address: (val) => ({
      start_location: normLocation(val, query.start_name),
    }),
    end_address: (val) => ({ end_location: normLocation(val, query.end_name) }),
    flight_datetime: (val) => normDate(val, 'flight_datetime'),
    pickup_datetime: (val) => normDate(val, 'pickup_datetime'),
    return_flight_datetime: (val) => normDate(val, 'return_flight_datetime'),
    return_pickup_datetime: (val) => normDate(val, 'return_pickup_datetime'),
    mode: (val) => ({ mode: `${val}`.toLowerCase() }),
    start_name: () => ({}),
    end_name: () => ({}),
  }

  const normQuery = normalize(query, transformers, allowedQueries)

  if (normQuery.campaign || normQuery.branch) {
    const marketing = {}
    if (query.campaign) marketing.campaign = query.campaign
    if (query.branch) marketing.branch = query.branch
    normQuery.marketing = marketing
    delete normQuery.campaign
    delete normQuery.branch
  }

  return normQuery
}
